/**
 * AccountMenu — identity + server-side logout.
 *
 * Shows the signed-in operator and their effective role at the current scope.
 * Logout goes through the BFF (/auth/logout) so the server clears the HttpOnly
 * session + back-channel — the browser never held a token to discard.
 */
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useSession } from "@/stores/session";
import { beginLogout } from "@/lib/api/client";

const ROLE_LABEL: Record<string, string> = {
  read_only: "Read only",
  site_operator: "Site manager",
  org_admin: "Customer admin",
  msp_admin: "MSP admin",
  platform_admin: "Platform admin",
};

export function AccountMenu() {
  const me = useSession((s) => s.me);
  const role = useSession((s) => s.effectiveRole());
  if (!me) return null;

  // Initials for the avatar chip (original mark, no gravatar/CDN fetch).
  const initials = me.user.display_name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className="inline-flex h-touch w-touch items-center justify-center rounded-full bg-brand-soft text-sm font-semibold text-brand"
        aria-label={`Account: ${me.user.display_name}`}
      >
        {initials}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-toast min-w-56 rounded-lg border border-line bg-overlay p-1 shadow-sheet"
        >
          <div className="px-3 py-2">
            <div className="text-sm font-medium text-ink">{me.user.display_name}</div>
            <div className="text-xs text-ink-muted">{me.user.email}</div>
            {role && (
              <div className="mt-1 text-xs text-ink-subtle">{ROLE_LABEL[role] ?? role}</div>
            )}
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Item
            onSelect={() => beginLogout()}
            className="flex min-h-touch cursor-pointer items-center rounded px-3 text-sm text-ink outline-none data-[highlighted]:bg-surface-2"
          >
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
